export const PAYMENT_RESULT_KEY = "PaymentResult";

const BOOKING_PAGES = {
  "1": {
    type: "Doctor appointment",
    cta: "/dashbord/booking",
    ctaLabel: "View appointment",
    retry: "/services",
  },
  "2": {
    type: "Lab test booking",
    cta: "/dashbord/booking-package",
    ctaLabel: "View lab booking",
    retry: "/packages",
  },
  "3": {
    type: "Medicine order",
    cta: "/dashbord/order",
    ctaLabel: "View order",
    retry: "/shop",
  },
  "4": {
    type: "Home care visit",
    cta: "/home-care/bookings",
    ctaLabel: "View home care booking",
    retry: "/home-care",
  },
  "5": {
    type: "Pay via App",
    cta: "/pay-app/history",
    ctaLabel: "View transaction",
    retry: "/pay-app",
  },
  "6": {
    type: "Wallet top-up",
    cta: "/dashbord/wallet",
    ctaLabel: "Open wallet",
    retry: "/dashbord/wallet",
  },
  "7": {
    type: "Home visit",
    cta: "/home-visit/bookings",
    ctaLabel: "View home visit",
    retry: "/home-visit",
  },
};

const PV_TYPES = {
  appointment: "Pay via App · Doctor appointment",
  lab: "Pay via App · Lab test",
  order: "Pay via App · Medicine order",
  hc: "Pay via App · Home care",
};

const GATEWAY_NAMES = {
  "1": "Razorpay",
  "3": "Stripe",
  "9": "Flutterwave",
  "11": "Cashfree",
  "16": "Cash",
};

const jobs = new Map();

export function runOnce(key, fn) {
  if (!jobs.has(key)) jobs.set(key, fn());
  return jobs.get(key);
}

export function parseJson(raw, fallback = null) {
  if (!raw) return fallback;
  try {
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return fallback;
  }
}

export function readPaymentData() {
  return parseJson(sessionStorage.getItem("PaymentData"), null);
}

export function readPaymentPage() {
  return sessionStorage.getItem("page") || "";
}

export function readPaymentGatewayId() {
  return sessionStorage.getItem("Paymentid") || "";
}

export function savePaymentResult(result) {
  try {
    sessionStorage.setItem(PAYMENT_RESULT_KEY, JSON.stringify(result));
  } catch {
    /* ignore quota */
  }
  return result;
}

export function readPaymentResult() {
  return parseJson(sessionStorage.getItem(PAYMENT_RESULT_KEY), null);
}

export function clearPaySession() {
  sessionStorage.removeItem("PaymentData");
  sessionStorage.removeItem("Paymentid");
  sessionStorage.removeItem("page");
}

export function continueAfterGateway(navigate, { success, transactionId, pending, cancelled } = {}) {
  const params = new URLSearchParams();
  if (pending) params.set("status", "pending");
  else if (cancelled) params.set("status", "cancelled");
  else params.set("status", success ? "true" : "false");
  params.set("transactionId", transactionId || (success ? "" : "0"));
  navigate(`/paymentresponse?${params.toString()}`, { replace: true });
}

export function presentResult(navigate, kind, fields = {}) {
  const page = fields.page || readPaymentPage();
  const data = fields.data || readPaymentData() || {};
  const currency = fields.currency || "₹";
  const gateway = fields.gateway || gatewayLabel(fields.gatewayId);
  const extra = {
    bookingId: fields.bookingId,
    appointmentId: fields.appointmentId,
    orderId: fields.orderId,
    txnId: fields.txnId,
    amount: fields.amount ?? pickAmount(data) ?? pickAmount(data.booking_payload),
  };
  const transactionId = fields.transactionId || (kind === "success" ? "local" : "0");
  const result = {
    kind,
    page,
    data,
    extra,
    transactionId,
    gateway,
    currency,
    message: fields.message || "",
    createdAt: Date.now(),
    rows: buildReceiptRows({ page, data, extra, currency, gateway, transactionId }),
    cta: ctaFor(page, extra, data),
    typeLabel: bookingTypeLabel(page, data),
  };
  savePaymentResult(result);
  if (kind !== "processing" && kind !== "pending") clearPaySession();
  const params = new URLSearchParams({
    status: kind === "success" || kind === "paid_unconfirmed" ? "true" : kind === "pending" ? "pending" : "false",
    transactionId: String(transactionId),
  });
  if (navigate) navigate(`/paymentresponse?${params.toString()}`, { replace: true });
  return result;
}

export function canReuseResult(existing, status, transactionId) {
  if (!existing?.kind || existing.kind === "processing") return false;
  const age = Date.now() - Number(existing.createdAt || 0);
  if (age > 45 * 60 * 1000) return false;
  if (transactionId && existing.transactionId && String(existing.transactionId) === String(transactionId)) {
    return true;
  }
  if (transactionId && existing.transactionId && String(existing.transactionId) !== String(transactionId)) {
    return false;
  }
  if (!status) return true;
  const classified = classifyQueryStatus(status, transactionId, "");
  if (classified === "success" && (existing.kind === "success" || existing.kind === "paid_unconfirmed")) return true;
  if (classified === "failed" && (existing.kind === "failed" || existing.kind === "cancelled")) return true;
  if (classified === "pending" && (existing.kind === "pending" || existing.kind === "uncertain")) return true;
  return false;
}

export function bookingTypeLabel(page, data) {
  if (String(page) === "5") {
    return PV_TYPES[data?.booking_type] || BOOKING_PAGES["5"].type;
  }
  return BOOKING_PAGES[String(page)]?.type || "Booking";
}

export function consultLabel(showType) {
  const n = String(showType);
  if (n === "1") return "In-clinic visit";
  if (n === "2") return "Video consultation";
  return "";
}

export function gatewayLabel(paymentId) {
  const id = paymentId || readPaymentGatewayId();
  return GATEWAY_NAMES[String(id)] || "Online payment";
}

export function formatMoney(amount, currency = "₹") {
  if (amount === undefined || amount === null || amount === "") return "";
  const n = Number(amount);
  if (Number.isNaN(n)) return `${currency} ${amount}`;
  return `${currency}${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

export function pickAmount(data) {
  if (!data || typeof data !== "object") return "";
  return data.tot_price ?? data.amount ?? data.payable_amount ?? data.total_amount ?? "";
}

export function ctaFor(page, extra = {}, data = {}) {
  const meta = BOOKING_PAGES[String(page)] || { cta: "/", ctaLabel: "Go home", retry: "/" };
  if (String(page) === "4" && (extra.bookingId || data.booking_id)) {
    return {
      href: `/home-care/booking/${extra.bookingId || data.booking_id}`,
      label: "View home care booking",
      retry: meta.retry,
    };
  }
  if (String(page) === "5" && (extra.txnId || data.txn_id)) {
    return {
      href: `/pay-app/txn/${extra.txnId || data.txn_id}`,
      label: "View transaction",
      retry: meta.retry,
    };
  }
  if (String(page) === "1" && extra.bookingId) {
    return { href: "/dashbord/booking", label: meta.ctaLabel, retry: meta.retry };
  }
  return { href: meta.cta, label: meta.ctaLabel, retry: meta.retry };
}

export function buildReceiptRows({ page, data, extra, currency, gateway, transactionId }) {
  const rows = [];
  const type = bookingTypeLabel(page, data);
  rows.push({ label: "Booking type", value: type });

  const consult = consultLabel(data?.show_type || data?.booking_payload?.show_type);
  if (consult) rows.push({ label: "Consult", value: consult });

  const doctor = data?.doctor_name || data?.booking_payload?.doctor_name;
  if (doctor) rows.push({ label: "Doctor", value: doctor });

  const hospital = data?.hospital_name || data?.booking_payload?.hospital_name;
  if (hospital) rows.push({ label: "Hospital", value: hospital });

  const lab = data?.lab_name;
  if (lab) rows.push({ label: "Lab", value: lab });

  const date = data?.date || data?.booking_payload?.date || data?.schedule_date;
  if (date) rows.push({ label: "Date", value: date });

  const time = data?.time || data?.booking_payload?.time || data?.schedule_time;
  if (time) rows.push({ label: "Time", value: time });

  if (data?.home_col_status == 1 || data?.booking_payload?.home_col_status == 1) {
    rows.push({ label: "Collection", value: "Home sample collection" });
  }

  const bookingId = extra?.bookingId || data?.booking_id || extra?.appointmentId;
  if (bookingId) rows.push({ label: "Booking ID", value: String(bookingId) });

  const orderId = extra?.orderId;
  if (orderId) rows.push({ label: "Order ID", value: String(orderId) });

  const txnId = extra?.txnId || data?.txn_id;
  if (txnId && String(page) === "5") rows.push({ label: "Pay via App ID", value: String(txnId) });

  if (transactionId && String(transactionId) !== "0") {
    rows.push({ label: "Payment reference", value: String(transactionId) });
  }

  if (gateway) rows.push({ label: "Paid with", value: gateway });

  const amount = extra?.amount ?? pickAmount(data) ?? pickAmount(data?.booking_payload);
  if (amount !== "" && amount !== undefined) {
    rows.push({ label: "Amount", value: formatMoney(amount, currency), emphasize: true });
  }

  return rows;
}

export function classifyQueryStatus(status, transactionId, paymentId) {
  const s = String(status || "").toLowerCase();
  const txn = String(transactionId || "").trim();
  const hasTxn = txn && txn !== "0";
  const isCash = String(paymentId) === "16";

  if (["false", "failed", "fail", "cancelled", "canceled", "declined"].includes(s)) {
    return "failed";
  }
  if (["pending", "processing", "user_dropped", "in_progress"].includes(s)) {
    return "pending";
  }
  if (["true", "success", "paid"].includes(s)) {
    if (!hasTxn && !isCash) return "uncertain";
    return "success";
  }
  if (!s) return "unknown";
  return "unknown";
}
